import { useLoadingState } from '@ipfabric/ui-components/hooks/useLoadingState';
import { withLoadingIndicator } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import { Iana, type NamedPathLookupAdvancedOptions } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import classNames from 'classnames';
import { Button } from 'components/Button/Button';
import { FormikMultiSelectField } from 'components/FormikMultiSelectField/FormikMultiSelectField';
import { FormikMultiTextField } from 'components/FormikMultiTextField/FormikMultiTextField';
import { FormikSelectField } from 'components/FormikSelectField/FormikSelectField';
import { FormikTextField } from 'components/FormikTextField/FormikTextField';
import { FormikToggleField } from 'components/FormikToggleField/FormikToggleField';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import { deleteAdvancedOptions } from 'components/Graph/commands/pathLookup/advancedOptions/deleteAdvancedOptions';
import { saveAdvancedOptions } from 'components/Graph/commands/pathLookup/advancedOptions/saveAdvancedOptions';
import { selectAdvancedOptions } from 'components/Graph/commands/pathLookup/advancedOptions/selectAdvancedOptions';
import { updateAdvancedOptions } from 'components/Graph/commands/pathLookup/advancedOptions/updateAdvancedOptions';
import { Select } from 'components/Select/Select';
import { useLocale } from 'containers/App/LocaleContext';
import { ConfirmDialog } from 'containers/ConfirmDialog/ConfirmDialog';
import type { FormikProps } from 'formik';
import { type JSX, useEffect, useMemo, useRef, useState } from 'react';
import type { SyntheticEvent } from 'react';
import deepEqual from 'react-fast-compare';
import { formikInputArrayToCsv } from 'utils/formik/valueAdapters/formikInputArrayToCsv';
import { useConfirmDialog } from 'utils/hooks/useConfirmDialog';
import { omit } from 'utils/object/omit';
import { makeValidateNumberCsv } from 'utils/validation/validateNumberCsv';
import { GraphAddPresetInput } from '../GraphAddPresetInput';
import type { MulticastFormValues } from './MulticastFormValues';
import { formValuesToAdvancedOptions } from './PathLookupAdvancedOptionsFormValues';
import type { UnicastFormValues } from './UnicastFormValues';

export const TTL_MIN = 1;
export const TTL_MAX = 255;
export const FRAGMENT_OFFSET_MIN = 0;
export const FRAGMENT_OFFSET_MAX = 8191; // (1 << 13) - 1;
const PORT_MIN = 0;
const PORT_MAX = 65535;

const DELETE_ADVANCED_OPTIONS_PRESET_CONFIRM_DIALOG =
  'graphSideDeleteAdvancedOptionsPresetConfirm';

export const validatePorts = makeValidateNumberCsv(PORT_MIN, PORT_MAX);

interface Props<TFormikValues> {
  resetAdvancedOptionsToSelectedPreset: () => void;
  formik: FormikProps<TFormikValues>;
  onProtocolChange: (value: string | null) => void;
}

const protocolOptions = [
  {
    label: 'TCP',
    value: 'tcp',
  },
  {
    label: 'UDP',
    value: 'udp',
  },
  {
    label: 'ICMP',
    value: 'icmp',
  },
  {
    label: 'ICMPv6',
    value: 'icmpv6',
  },
];

const flagsOptions = [
  { label: 'ACK', value: 'ack' },
  { label: 'FIN', value: 'fin' },
  { label: 'SYN', value: 'syn' },
  { label: 'RST', value: 'rst' },
  { label: 'PSH', value: 'psh' },
  { label: 'URG', value: 'urg' },
];

const relevantErrorKeys = [
  'srcRegions',
  'dstRegions',
  'ttl',
  'fragmentOffset',
  'applications',
  'tracked',
  'dstPorts',
  'srcPorts',
  'flags',
  'icmpOptions',
  'protocol',
];

const Separator = () => (
  <div className='mb-5 border-0 border-b border-solid border-gray-600' />
);

export const PathLookupAdvancedOptions = <
  TFormikValues extends MulticastFormValues | UnicastFormValues,
>(
  props: Props<TFormikValues>,
): JSX.Element => {
  const {
    canvasGraph: {
      graphSidePanel: {
        pathlookup: { advancedOptions: advancedOptionsMsg },
      },
    },
  } = useLocale();
  const ctxProps = useGraphContextSelectors({
    advancedOptions: prop('pathlookup', 'advancedOptions'),
    dispatch: prop('dispatch'),
    selectedAdvancedOptions: prop('pathlookup', 'selectedAdvancedOptions'),
  });
  const [saveAsOpen, setSaveAsOpen] = useState<boolean>();
  const [open, setOpen] = useState<boolean>(false);
  const [saving, setSavingState] = useLoadingState();
  const [savingAs, setSavingAsState] = useLoadingState();
  const deletePresetConfirmDialog = useConfirmDialog({
    closeOnUnmount: true,
    name: DELETE_ADVANCED_OPTIONS_PRESET_CONFIRM_DIALOG,
  });
  const containerRef = useRef<HTMLDivElement>(null);

  const modified = useMemo(
    () =>
      !deepEqual(
        omit(['name', 'id', 'factory'], ctxProps.selectedAdvancedOptions),
        formValuesToAdvancedOptions(props.formik.values),
      ),
    [props.formik.values, ctxProps.selectedAdvancedOptions],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies(props.formik.values): somehow needed to add * to modified preset label
  const advancedOptions = useMemo(
    () =>
      ctxProps.advancedOptions.map((a) => ({
        label: a.name,
        value: a,
      })),
    [ctxProps.advancedOptions, props.formik.values],
  );

  const isAdvancedOptionsSubformValid = () =>
    !Object.keys(props.formik.errors).some((errKey) =>
      relevantErrorKeys.includes(errKey),
    );

  const handleSelectAdvancedOptions = (
    value: NamedPathLookupAdvancedOptions | null,
  ) => {
    if (value) {
      ctxProps.dispatch(selectAdvancedOptions, { advancedOptions: value });
    }
  };

  const handleDelete = (e: SyntheticEvent) => {
    e.preventDefault();
    deletePresetConfirmDialog.toggleConfirmDialog();
  };

  const handleDeleteConfirm = async () => {
    await ctxProps.dispatch(deleteAdvancedOptions, {
      advancedOptions: ctxProps.selectedAdvancedOptions,
    });
    deletePresetConfirmDialog.closeConfirmDialog();
  };

  const handleSaveAs = async (name: string) => {
    for (const key of relevantErrorKeys) {
      props.formik.setFieldTouched(key, true);
    }

    if (isAdvancedOptionsSubformValid()) {
      await withLoadingIndicator(setSavingAsState)(async () => {
        await ctxProps.dispatch(saveAdvancedOptions, {
          advancedOptions: formValuesToAdvancedOptions(props.formik.values),
          name,
        });

        setSaveAsOpen(false);
      });
    }
  };

  const handleUpdate = (e: SyntheticEvent) => {
    e.preventDefault();
    for (const key of relevantErrorKeys) {
      props.formik.setFieldTouched(key, true);
    }

    if (isAdvancedOptionsSubformValid()) {
      withLoadingIndicator(setSavingState)(async () => {
        await ctxProps.dispatch(updateAdvancedOptions, {
          originalAdvancedOptions: ctxProps.selectedAdvancedOptions,
          newAdvancedOptions: formValuesToAdvancedOptions(props.formik.values),
        });
      });
    }
  };

  const icmpOptions = useMemo(() => {
    return Iana.icmpTypesCodes()
      .map((icmp) => ({
        label: icmp.name,
        value: { type: icmp.type, code: icmp.code },
      }))
      .sort(
        // move echo request & echo reply to top
        (a) =>
          (a.value.type === 0 && a.value.code === 0) ||
          (a.value.type === 8 && a.value.code === 0)
            ? -1
            : 0,
      );
  }, []);

  const icmpv6Options = useMemo(() => {
    return Iana.icmp6TypesCodes()
      .map((icmp) => ({
        label: icmp.name,
        value: { type: icmp.type, code: icmp.code },
      }))
      .sort(
        // move echo request & echo reply to top
        (a) =>
          (a.value.type === 0 && a.value.code === 0) ||
          (a.value.type === 128 && a.value.code === 0)
            ? -1
            : 0,
      );
  }, []);

  const toggleOpen = (e: SyntheticEvent) => {
    e.preventDefault();

    setOpen(!open);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: trigger on submit
  useEffect(() => {
    if (!isAdvancedOptionsSubformValid()) {
      setOpen(true);
    }
  }, [props.formik.submitCount]);

  return (
    <div ref={containerRef}>
      <ConfirmDialog
        id={DELETE_ADVANCED_OPTIONS_PRESET_CONFIRM_DIALOG}
        keepOnConfirm
        msg={advancedOptionsMsg.deleteConfirm}
        onClickConfirm={handleDeleteConfirm}
        onClickSecondaryConfirm={() => {
          deletePresetConfirmDialog.closeConfirmDialog();
        }}
        btnConfirmVariant='primary'
        btnConfirmAccent='negative'
        btnCancelVariant='secondary'
        btnCancelAccent='positive'
      />
      <div className='bg-gray-100 px-5 py-4'>
        <label className='form__label' htmlFor={undefined}>
          {advancedOptionsMsg.advancedPacketOptions}
        </label>
        <Select
          options={advancedOptions}
          onChange={handleSelectAdvancedOptions}
          value={ctxProps.selectedAdvancedOptions}
          getOptionValue={(value) => value?.id ?? 'null'}
          nonClearable
          searchable
          formatValueLabel={modified ? (label) => `${label} *` : undefined}
        />
        <div className='flex justify-end pt-3.5'>
          {modified && (
            <Button
              className='mr-auto'
              type='button'
              variant='tertiary'
              onClick={() => {
                props.resetAdvancedOptionsToSelectedPreset();
              }}
            >
              {advancedOptionsMsg.discardChanges}
            </Button>
          )}
          <Button variant='tertiary' onClick={toggleOpen} type='button'>
            {advancedOptionsMsg.details[open ? 'lessDetails' : 'moreDetails']}
            <i
              className={classNames(
                'fa text-xxs ml-1 pt-1 align-top',
                open ? 'fa-chevron-up' : 'fa-chevron-down',
              )}
            />
          </Button>
        </div>
        {open && (
          <>
            <div className='flex'>
              <div className='w-32 pr-2.5'>
                <FormikSelectField
                  label={advancedOptionsMsg.protocol}
                  formik={props.formik}
                  name='protocol'
                  onChange={props.onProtocolChange}
                  options={protocolOptions}
                  nonClearable
                />
              </div>
              <div className='w-full min-w-0'>
                {props.formik.values.protocol === 'tcp' && (
                  <FormikMultiSelectField
                    label={advancedOptionsMsg.tcpFlags}
                    placeholder={advancedOptionsMsg.none}
                    disabled={props.formik.values.protocol !== 'tcp'}
                    formik={props.formik}
                    name='flags'
                    options={flagsOptions}
                  />
                )}
                {props.formik.values.protocol === 'icmp' && (
                  <FormikSelectField
                    label={advancedOptionsMsg.icmpOptions}
                    formik={props.formik}
                    name='icmpOptions'
                    options={icmpOptions}
                    getOptionValue={(value) =>
                      value ? `s/${value.type}-${value.code}` : 'null'
                    }
                    nonClearable
                    searchable
                  />
                )}
                {props.formik.values.protocol === 'icmpv6' && (
                  <FormikSelectField
                    label={advancedOptionsMsg.icmpv6Options}
                    formik={props.formik}
                    name='icmpOptions'
                    options={icmpv6Options}
                    getOptionValue={(value) =>
                      value ? `s/${value.type}-${value.code}` : 'null'
                    }
                    nonClearable
                    searchable
                  />
                )}
              </div>
            </div>
            <Separator />
            {props.formik.values.protocol !== 'icmp' &&
              props.formik.values.protocol !== 'icmpv6' && (
                <>
                  <FormikMultiTextField
                    label={advancedOptionsMsg.sourcePorts}
                    formik={props.formik}
                    name='srcPorts'
                    transformValue={formikInputArrayToCsv}
                    validateItem={validatePorts}
                    container={containerRef.current}
                  />
                  <FormikMultiTextField
                    label={advancedOptionsMsg.destinationPorts}
                    formik={props.formik}
                    name='dstPorts'
                    transformValue={formikInputArrayToCsv}
                    validateItem={validatePorts}
                    container={containerRef.current}
                  />
                  <Separator />
                </>
              )}
            <div className='flex'>
              <div className='mr-2.5 w-full'>
                <FormikTextField
                  label={advancedOptionsMsg.ttl}
                  formik={props.formik}
                  name='ttl'
                  min={TTL_MIN}
                  max={TTL_MAX}
                  type='number'
                />
              </div>
              <div className='w-full'>
                <FormikTextField
                  label={advancedOptionsMsg.fragmentOffset}
                  formik={props.formik}
                  name='fragmentOffset'
                  min={FRAGMENT_OFFSET_MIN}
                  max={FRAGMENT_OFFSET_MAX}
                  type='number'
                />
              </div>
            </div>
            <Separator />
            <div className='flex'>
              <div className='w-full'>
                <FormikTextField
                  label='Site category'
                  formik={props.formik}
                  name='category'
                  type='text'
                  placeholder='Any site category'
                />
              </div>
            </div>
            <div className='flex'>
              <div className='w-full'>
                <FormikTextField
                  label='URL / DOMAIN'
                  formik={props.formik}
                  name='url'
                  type='text'
                  placeholder='Any URL or domain'
                />
              </div>
            </div>
            <Separator />
            <div className='flex'>
              <div className='w-full'>
                <FormikTextField
                  label={advancedOptionsMsg.application}
                  formik={props.formik}
                  name='applications'
                  type='text'
                  placeholder={advancedOptionsMsg.anyApplication}
                />
              </div>
            </div>
            <div className='mb-5'>
              <FormikToggleField
                formik={props.formik}
                label={advancedOptionsMsg.applicationRegexMatching}
                labelPosition='right'
                name='applicationsMatchWithRegex'
              />
            </div>
            <Separator />
            <FormikToggleField
              formik={props.formik}
              label={advancedOptionsMsg.enableRegions}
              labelPosition='right'
              name='enableRegions'
            />
            <div className='mt-4 flex'>
              <div className='w-full pr-2.5'>
                <FormikTextField
                  disabled={!props.formik.values.enableRegions}
                  label={advancedOptionsMsg.sourceRegions}
                  formik={props.formik}
                  name='srcRegions'
                  type='text'
                  placeholder={advancedOptionsMsg.anyRegion}
                />
              </div>
              <div className='w-full'>
                <FormikTextField
                  disabled={!props.formik.values.enableRegions}
                  label={advancedOptionsMsg.destinationRegions}
                  formik={props.formik}
                  name='dstRegions'
                  type='text'
                  placeholder={advancedOptionsMsg.anyRegion}
                />
              </div>
            </div>
            <FormikToggleField
              label={advancedOptionsMsg.tracked}
              formik={props.formik}
              name='tracked'
              labelPosition='right'
            />
            <div className='mt-5'>
              {saveAsOpen ? (
                <GraphAddPresetInput
                  onSaveClick={handleSaveAs}
                  onCloseClick={() => {
                    setSaveAsOpen(false);
                  }}
                  saving={savingAs === 'LOADING'}
                />
              ) : (
                <div className='flex justify-between gap-2'>
                  <Button
                    className='flex-1'
                    disabled={ctxProps.selectedAdvancedOptions.factory}
                    variant='secondary'
                    onClick={handleDelete}
                    type='button'
                  >
                    {advancedOptionsMsg.delete}
                  </Button>
                  <Button
                    className='flex-1'
                    variant='secondary'
                    onClick={() => {
                      setSaveAsOpen(true);
                    }}
                    type='button'
                  >
                    {advancedOptionsMsg.saveAs}
                  </Button>
                  <Button
                    className='flex-1'
                    disabled={ctxProps.selectedAdvancedOptions.factory}
                    variant='secondary'
                    onClick={handleUpdate}
                    loading={saving === 'LOADING'}
                    type='button'
                  >
                    {advancedOptionsMsg.save}
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
